import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Shield, Lock, ArrowRight } from "lucide-react";
import { GuardianSignalCard, GuardianOps, GuardianEquityChart, GuardianYearly } from "../components/Guardian";
import { PriceChart } from "../components/PriceChart";

const DIR_COLOR = { LONG: "text-sky-400", SHORT: "text-red-400" };

export default function GuardianReports() {
  const { user } = useAuth();
  const { lang } = useLang();
  const it = lang === "it";
  const access = user?.guardian_access || user?.role === "admin";
  const currentYear = new Date().getFullYear();
  const [signal, setSignal] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!access) return;
    api.get("/guardian/signals/today").then((r) => setSignal(r.data.signal)).catch(() => {});
    api.get("/guardian/signals", { params: { limit: 90 } }).then((r) => setHistory(r.data.signals)).catch(() => {});
  }, [access]);

  return (
    <div data-testid="guardian-reports-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-sky-400 font-mono mb-3 flex items-center gap-2">
        <Shield className="w-4 h-4" /> {it ? "Sistema conservativo" : "Conservative system"}
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-white">
        Trading Room <span className="text-sky-400">Guardian</span>
      </h1>
      <p className="text-slate-400 mt-3 max-w-2xl">
        {it
          ? "Il segnale serale, lo storico dei segnali e tutte le operazioni del sistema che in 18 anni non ha mai chiuso un anno in perdita."
          : "The evening signal, the signal history and every operation of the system that never closed a losing year in 18 years."}
      </p>

      {access ? (
        <div className="mt-10 space-y-8">
          <div className="grid lg:grid-cols-3 gap-6">
            {signal && <GuardianSignalCard signal={signal} />}
            <div className={signal ? "lg:col-span-2" : "lg:col-span-3"}>
              <GuardianOps />
            </div>
          </div>
          <GuardianEquityChart year={currentYear} />
          <PriceChart system="guardian" title={it ? "Grafico Bund con segnali Guardian" : "Bund chart with Guardian signals"} />
          <GuardianYearly />
          <div data-testid="guardian-signals-history" className="border border-sky-500/20 bg-[#0B1220]">
            <div className="px-4 py-3 border-b border-sky-500/15">
              <h2 className="text-sm font-medium text-slate-200">{it ? "Storico segnali (ultimi 90)" : "Signal history (last 90)"}</h2>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-xs font-mono">
                <thead className="sticky top-0 bg-[#0F172A]">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-4 py-2">{it ? "Data" : "Date"}</th>
                    <th className="text-left px-2 py-2">{it ? "Segnale" : "Signal"}</th>
                    <th className="text-right px-2 py-2">Z</th>
                    <th className="text-right px-2 py-2">Regime</th>
                    <th className="text-right px-2 py-2">YTD €</th>
                    <th className="text-left px-4 py-2">{it ? "Motivazione" : "Motivation"}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((s) => (
                    <tr key={s.id} className="border-t border-white/5">
                      <td className="px-4 py-2 text-slate-400">{s.date}</td>
                      <td className={`px-2 py-2 font-bold ${DIR_COLOR[s.direction] || "text-slate-300"}`}>{s.direction}</td>
                      <td className="px-2 py-2 text-right text-slate-300">{s.z_score > 0 ? "+" : ""}{s.z_score}</td>
                      <td className={`px-2 py-2 text-right ${DIR_COLOR[s.regime] || "text-slate-400"}`}>{s.regime}</td>
                      <td className={`px-2 py-2 text-right ${s.ytd_eur >= 0 ? "text-sky-400" : "text-red-400"}`}>
                        {s.ytd_eur > 0 ? "+" : ""}{Math.round(s.ytd_eur).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-slate-400 font-sans max-w-md truncate" title={it ? s.motivation_it : s.motivation_en}>
                        {it ? s.motivation_it : s.motivation_en}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-10 space-y-6">
          <div data-testid="guardian-reports-locked" className="border border-sky-500/30 bg-sky-500/5 p-6 flex flex-wrap items-center gap-4">
            <Lock className="w-5 h-5 text-sky-400 shrink-0" />
            <div className="flex-1 min-w-[240px]">
              <h2 className="text-sm font-medium text-white">
                {it ? "La Trading Room Guardian è riservata agli abbonati Guardian" : "The Guardian Trading Room is reserved for Guardian subscribers"}
              </h2>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                {it
                  ? "Il segnale serale e l'operazione in corso sono in chiaro solo per gli abbonati (abbonamento separato, solo annuale, trial non ammesso). Qui sotto puoi vedere l'ultima operazione chiusa; quella in corso resta riservata."
                  : "The evening signal and the ongoing operation are only visible to subscribers (separate subscription, annual only, no trial). Below you can see the last closed operation; the ongoing one stays reserved."}
              </p>
            </div>
            <Link to="/guardian#guardian-offer" data-testid="guardian-reports-cta"
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-black font-medium rounded px-5 py-2.5 text-sm transition-colors">
              {it ? "Abbonati a Guardian" : "Subscribe to Guardian"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <GuardianOps />
          <GuardianEquityChart year={currentYear} />
          <PriceChart system="guardian" title={it ? "Grafico Bund con segnali Guardian" : "Bund chart with Guardian signals"} />
          <GuardianYearly />
        </div>
      )}
    </div>
  );
}
